import { allPages } from "@nexxt/app-kit";
import type { ListAttributeCompletenessInput, ListAttributeTasksInput } from "@nexxt/contracts/catalog";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { catalogClient, type SkuAttributes } from "../client";

// Server state of the attribute tasks and of their SKUs (pattern.md §10): one factory, the same keys for loaders,
// screens and mutations.

export type { AttributeCompleteness, AttributeOptions, AttributeTask, SkuAttributes } from "../client";

/** The filters of the list input (`listAttributeTasksInput`), without the paging the queries add. */
export type TaskFilters = Pick<
  ListAttributeTasksInput,
  "status" | "attributesStatus" | "brand" | "linesheet" | "q" | "sort"
>;

/** The filters of the completeness by brand (ADR-223), without the paging. */
export type CompletenessFilters = Pick<ListAttributeCompletenessInput, "brand" | "season">;

/** Which SKUs of a task the grid shows: all of them, or the ones still to work on. */
export const skuViews = ["all", "incomplete", "unlocked"] as const;
export type SkuView = (typeof skuViews)[number];

/** How an SKU is named where a row has to be told from the others: style, colour and size. */
export const skuName = (sku: SkuAttributes): string =>
  [sku.styleCode, sku.colourDescription, sku.size].filter(Boolean).join(" · ");

export interface SkuFilters {
  view?: SkuView | undefined;
  q?: string | undefined;
}

const viewQuery: Record<SkuView, { onlyIncomplete?: boolean; onlyUnlocked?: boolean }> = {
  all: {},
  incomplete: { onlyIncomplete: true },
  unlocked: { onlyUnlocked: true },
};

const PAGE_SIZE = 50;

const list = (filters: TaskFilters, page: number, pageSize: number, signal: AbortSignal) =>
  catalogClient().listAttributeTasks({ ...filters, page, pageSize }, signal);

/** Every SKU of a task of a view: what an export writes. */
export const allSkus = (taskId: string, view: SkuView): Promise<SkuAttributes[]> =>
  allPages<SkuAttributes>((cursor) =>
    catalogClient().listTaskSkus({ taskId, ...viewQuery[view], ...(cursor && { cursor }), limit: 500 }),
  );

const keys = {
  all: ["attribute-tasks"] as const,
  /** Every cached list of SKUs of a task: a write patches them all. */
  skusOf: (taskId: string) => ["attribute-tasks", "skus", taskId] as const,
};

export const attributeQueries = {
  ...keys,

  list: (filters: TaskFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.all, "list", filters],
      queryFn: ({ pageParam, signal }) => list(filters, pageParam, PAGE_SIZE, signal),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  /** Completeness by brand, the nearest due date first (ADR-223): under `all`, so a write on a task reloads it. */
  completeness: (filters: CompletenessFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.all, "completeness", filters],
      queryFn: ({ pageParam, signal }) =>
        catalogClient().listAttributeCompleteness({ ...filters, page: pageParam, pageSize: PAGE_SIZE }, signal),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  /** The task of a linesheet, or null while nobody opened one. */
  ofLinesheet: (linesheetId: string) =>
    queryOptions({
      queryKey: [...keys.all, "of-linesheet", linesheetId],
      queryFn: async ({ signal }) => (await list({ linesheet: linesheetId }, 1, 1, signal)).items[0] ?? null,
    }),

  detail: (taskId: string) =>
    queryOptions({
      queryKey: [...keys.all, "detail", taskId],
      queryFn: ({ signal }) => catalogClient().getAttributeTask({ taskId }, signal),
    }),

  skus: (taskId: string, { view = "all", q }: SkuFilters = {}) =>
    infiniteQueryOptions({
      queryKey: [...keys.skusOf(taskId), { view, q }],
      queryFn: ({ pageParam, signal }) =>
        catalogClient().listTaskSkus(
          { taskId, ...viewQuery[view], ...(q && { q }), ...(pageParam && { cursor: pageParam }), limit: 100 },
          signal,
        ),
      initialPageParam: "",
      getNextPageParam: (last) => last.nextCursor ?? undefined,
    }),

  /** The values of the attribute grid (ADR-048): they change rarely. */
  options: () =>
    queryOptions({
      queryKey: [...keys.all, "options"],
      queryFn: ({ signal }) => catalogClient().getAttributeOptions({}, signal),
      staleTime: 10 * 60_000,
    }),
};
